import React, { useState, useEffect, useContext } from 'react';
import { apiClient, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, MapPin, XCircle, CheckCircle, Truck, User, Phone, Envelope } from '@phosphor-icons/react';

const OrdersPage = () => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingData, setTrackingData] = useState(null);
  const [showTracking, setShowTracking] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      let url = '/orders';
      if (user?.role === 'admin') {
        url = '/admin/orders';
      }
      const response = await apiClient.get(url);
      const orderData = response.data.orders || [];
      setOrders(orderData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    }
    finally { setLoading(false); }
  };

  const trackOrder = async (orderId) => {
    try {
      // Admin needs a different approach - track via admin endpoint
      if (user?.role === 'admin') {
        const order = orders.find(o => o.id === orderId);
        const steps = ["Order Placed", "Confirmed", "Processing", "Shipped", "Delivered"];
        const statusMap = { pending: 0, confirmed: 1, processing: 2, shipped: 3, delivered: 4, cancelled: -1 };
        const currentStep = statusMap[order?.status] ?? 0;
        const timeline = currentStep >= 0
          ? steps.map((s, i) => ({ status: s, done: i <= currentStep }))
          : [{ status: "Order Placed", done: true }, { status: "Cancelled", done: true }];
        setTrackingData({ order, timeline });
      } else {
        const response = await apiClient.get(`/orders/${orderId}/track`);
        setTrackingData(response.data);
      }
      setShowTracking(true);
    } catch (error) { toast.error('Failed to load tracking info'); }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      if (user?.role === 'admin') {
        await apiClient.put(`/admin/orders/${orderId}/status?status=cancelled`);
      } else {
        await apiClient.post(`/orders/${orderId}/cancel`);
      }
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel order'); }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await apiClient.put(`/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const viewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-500/20 text-yellow-400', confirmed: 'bg-blue-500/20 text-blue-400', processing: 'bg-indigo-500/20 text-indigo-400', shipped: 'bg-purple-500/20 text-purple-400', delivered: 'bg-green-500/20 text-green-400', cancelled: 'bg-red-500/20 text-red-400' };
    return colors[status] || 'bg-slate-500/20 text-slate-400';
  };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-slate-400">Loading orders...</p></div>;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <h1 className="text-4xl sm:text-5xl font-medium mb-2 text-foreground" data-testid="orders-heading">
          {user?.role === 'admin' ? 'All Orders' : 'My Orders'}
        </h1>
        {user?.role === 'admin' && <p className="text-sm text-slate-400 mb-8">Manage and track all customer orders</p>}

        {/* Tracking Dialog */}
        <Dialog open={showTracking} onOpenChange={setShowTracking}>
          <DialogContent className="bg-card border-border max-w-md" aria-describedby="tracking-desc" data-testid="tracking-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Order Tracking</DialogTitle>
              <p id="tracking-desc" className="text-sm text-slate-400">
                Order #{trackingData?.order?.id?.slice(0, 8)} | {trackingData?.order?.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
              </p>
            </DialogHeader>
            {trackingData && (
              <div className="mt-4 space-y-1">
                {trackingData.timeline.map((step, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`tracking-step-${index}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done ? 'bg-[#D4AF37] text-white' : 'bg-secondary text-slate-500'}`}>
                        {step.done ? <CheckCircle size={18} weight="fill" /> : <span className="text-xs">{index + 1}</span>}
                      </div>
                      {index < trackingData.timeline.length - 1 && <div className={`w-0.5 h-8 ${step.done ? 'bg-[#D4AF37]' : 'bg-secondary'}`} />}
                    </div>
                    <div className="pt-1"><p className={`text-sm font-medium ${step.done ? 'text-foreground' : 'text-slate-500'}`}>{step.status}</p></div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Order Detail Dialog */}
        <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="order-detail-desc" data-testid="order-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Order Details</DialogTitle>
              <p id="order-detail-desc" className="text-sm text-slate-400">Order #{selectedOrder?.id?.slice(0, 8)}</p>
            </DialogHeader>
            {selectedOrder && (
              <div className="mt-4 space-y-4">
                {/* Customer Info */}
                <div className="bg-background border border-border p-4">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><User size={16} className="text-[#D4AF37]" /> Customer Information</h4>
                  <p className="text-sm text-slate-300">{selectedOrder.user_email}</p>
                </div>
                {/* Shipping Address */}
                {selectedOrder.shipping_address && (
                  <div className="bg-background border border-border p-4">
                    <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" /> Shipping Address</h4>
                    <p className="text-sm text-slate-300">{selectedOrder.shipping_address.name}</p>
                    <p className="text-sm text-slate-400">{selectedOrder.shipping_address.address}</p>
                    <p className="text-sm text-slate-400">{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zipCode}</p>
                    <p className="text-sm text-slate-400">{selectedOrder.shipping_address.country}</p>
                  </div>
                )}
                {/* Items */}
                <div className="bg-background border border-border p-4">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><Package size={16} className="text-[#D4AF37]" /> Order Items</h4>
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
                      <span className="text-slate-300">{item.product_name} x{item.quantity} ({item.size})</span>
                      <span className="text-[#D4AF37]">${((item.price || 0) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {/* Payment & Total */}
                <div className="bg-background border border-border p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Payment Method</span>
                    <span className="text-foreground">{selectedOrder.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment'}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Payment Status</span>
                    <span className="text-foreground">{selectedOrder.payment_status}</span>
                  </div>
                  {(selectedOrder.discount_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-400">Discount {selectedOrder.coupon_code && `(${selectedOrder.coupon_code})`}</span>
                      <span className="text-green-400">-${(selectedOrder.discount_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-medium pt-2 border-t border-border">
                    <span className="text-foreground">Total</span>
                    <span className="text-[#D4AF37]">${(selectedOrder.final_amount || selectedOrder.total_amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {orders.length === 0 ? (
          <div className="text-center py-24 bg-card border border-border" data-testid="no-orders-message">
            <Package size={64} weight="thin" className="mx-auto mb-6 text-slate-400" />
            <h2 className="text-2xl font-normal mb-4 text-foreground">No orders yet</h2>
            <p className="text-slate-400">Your order history will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((order, index) => (
              <div key={order.id || index} className="bg-card border border-border p-6" data-testid={`order-card-${index}`}>
                {/* Order Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-foreground font-medium" data-testid={`order-id-${index}`}>Order #{(order.id || '').slice(0, 8)}</p>
                      <span className={`text-xs uppercase px-2 py-0.5 rounded ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {order.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment'} | {order.payment_status}
                    </p>
                  </div>
                  <p className="text-xl font-medium text-[#D4AF37]">${(order.final_amount || order.total_amount || 0).toFixed(2)}</p>
                </div>

                {/* Customer & Shipping Info (Admin view) */}
                {user?.role === 'admin' && (
                  <div className="bg-background border border-border p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Customer</p>
                      <p className="text-sm text-foreground flex items-center gap-1"><Envelope size={14} className="text-[#D4AF37]" /> {order.user_email}</p>
                    </div>
                    {order.shipping_address && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Shipping To</p>
                        <p className="text-sm text-foreground">{order.shipping_address.name}</p>
                        <p className="text-xs text-slate-400">{order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Items */}
                <div className="border-t border-border pt-3 mb-4">
                  {order.items?.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex justify-between text-sm py-1">
                      <span className="text-slate-300">{item.product_name} x{item.quantity} ({item.size})</span>
                      <span className="text-slate-300">${((item.price || 0) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {(order.discount_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm text-green-400 pt-1">
                      <span>Discount {order.coupon_code && `(${order.coupon_code})`}</span>
                      <span>-${(order.discount_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => viewOrderDetail(order)} data-testid={`view-order-${index}`}>
                    <Package size={15} className="mr-1" /> View Details
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => trackOrder(order.id)} data-testid={`track-order-${index}`}>
                    <Truck size={15} className="mr-1" /> Track Order
                  </Button>
                  {!['shipped', 'delivered', 'cancelled'].includes(order.status) && (
                    <Button variant="outline" size="sm" className="rounded-sm text-red-400 hover:text-red-300" onClick={() => cancelOrder(order.id)} data-testid={`cancel-order-${index}`}>
                      <XCircle size={15} className="mr-1" /> Cancel
                    </Button>
                  )}
                  {/* Admin: Quick status update buttons */}
                  {user?.role === 'admin' && !['delivered', 'cancelled'].includes(order.status) && (
                    <>
                      {order.status === 'pending' && <Button size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-700 text-xs" onClick={() => updateStatus(order.id, 'confirmed')}>Confirm</Button>}
                      {order.status === 'confirmed' && <Button size="sm" className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={() => updateStatus(order.id, 'processing')}>Start Processing</Button>}
                      {order.status === 'processing' && <Button size="sm" className="rounded-sm bg-purple-600 hover:bg-purple-700 text-xs" onClick={() => updateStatus(order.id, 'shipped')}>Mark Shipped</Button>}
                      {order.status === 'shipped' && <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700 text-xs" onClick={() => updateStatus(order.id, 'delivered')}>Mark Delivered</Button>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
